import React, { useState } from 'react';
import { Modal, Typography, Space, Alert } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { contactService } from '../../../services/api/contactService';
import type { ProducerContact } from '../../../types/contact';

const { Text } = Typography;

interface DeleteContactModalProps {
  visible: boolean;
  contact: ProducerContact | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * DeleteContactModal Component
 * 
 * Modal for confirming deletion of a producer-contact relationship.
 * Displays the contact's full name and a warning message.
 * Performs soft delete via API (marks relationship as inactive).
 * 
 * Features:
 * - Confirmation dialog with contact name
 * - Warning message about irreversible action
 * - Cancel and Delete buttons
 * - Error handling
 * - Loading state during deletion
 */
export const DeleteContactModal: React.FC<DeleteContactModalProps> = ({
  visible,
  contact,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handle contact deletion
   * Calls API to soft delete the producer-contact relationship
   */
  const handleDelete = async () => {
    if (!contact) return;

    setLoading(true);
    setError(null);

    try {
      // Delete contact relationship via API
      await contactService.deleteProducerContact(contact.id);
      
      // Close modal and trigger success callback
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      setError(err.message || t('contacts.error.delete'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal cancel
   * Resets error state and closes modal
   */
  const handleCancel = () => {
    setError(null);
    onClose();
  };

  // Don't render if no contact is provided
  if (!contact) return null;

  const fullName = `${contact.contact.name} ${contact.contact.surname}`;

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          {t('contacts.delete.title')}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleDelete}
      okText={t('contacts.delete.button')}
      cancelText={t('common.cancel')}
      confirmLoading={loading}
      okButtonProps={{ danger: true }}
      width={500}
    >
      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* Contact name display */}
        <div>
          <Text>{t('contacts.delete.confirm')}</Text>
        </div>

        <div style={{
          padding: 12,
          background: '#fafafa',
          borderRadius: 4,
          border: '1px solid #d9d9d9'
        }}>
          <Text strong style={{ fontSize: 16 }}>
            {fullName}
          </Text>
          {contact.position && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">
                {contact.position.name}
              </Text>
            </div>
          )}
        </div>

        {/* Warning message */}
        <Alert
          message={t('contacts.delete.warning')}
          type="warning"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default DeleteContactModal;
